import { Injectable, Inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, of, Subscription } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { CommissionReportsService } from './commission-reports.service';
import { SelectedDnService } from './selected-dn.service';
import { BaseApiService } from './base-api.service';

export interface ChatbotResponse {
  response: string;
  session_id: string;
}

export interface ChatbotRequest {
  CD_DN: string;
  NM_GRP_COMISSION: string;
  question: string;
  session_id: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatbotService extends BaseApiService implements OnDestroy {
  // Cache do grupo de comissão
  private cachedGrupoComission: string | null = null;
  private dnSubscription: Subscription;

  constructor(
    http: HttpClient,
    private commissionReportsService: CommissionReportsService,
    private selectedDnService: SelectedDnService,
    // ...existing code...
  ) {
    super(http);
    // Escuta mudanças no DN para limpar o cache
    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe(() => {
      this.clearGrupoCache();
    });
  }

  ngOnDestroy(): void {
    if (this.dnSubscription) {
      this.dnSubscription.unsubscribe();
    }
  }

  /**
   * Limpa o cache do grupo de comissão
   */
  clearGrupoCache(): void {
    if (this.cachedGrupoComission) {
      if (this.configService.isDebugEnabled()) {
        console.log('🔄 Cache do grupo de comissão limpo (DN alterado)');
      }
    }
    this.cachedGrupoComission = null;
  }

  // Método para gerar session ID no formato correto: grupo_dn_cd_dn
  private generateSessionId(grupoComission?: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    // Formato: grupo_dn_cd_dn_[timestamp]_[random]
    const grupo = grupoComission ? grupoComission.toLowerCase() : 'grupo';
    const cdDn = this.selectedDnService.getSelectedDn();
    return `${grupo}_${cdDn}_${timestamp}_${random}`;
  }

  // Método para garantir que sempre temos um novo session ID
  getNewSessionId(): string {
    return this.generateSessionId();
  }

  // Método para obter o CD_DN
  getCdDn(): string {
    return this.selectedDnService.getSelectedDn();
  }

  // Método para obter o grupo (com cache)
  getGrupoComission(): Observable<string> {
    // Se já tem cache, retorna imediatamente
    if (this.cachedGrupoComission) {
      if (this.configService.isDebugEnabled()) {
        console.log('✅ Usando grupo de comissão do cache:', this.cachedGrupoComission);
      }
      return of(this.cachedGrupoComission);
    }

    const dn = this.selectedDnService.getSelectedDnAsNumber();
    if (!dn || dn === 0) {
      return throwError(() => new Error('DN não selecionado ou inválido. Selecione um DN antes de obter o grupo de comissão.'));
    }

  if (this.configService.isDebugEnabled()) {
      console.log('🔍 Buscando grupo de comissão da API para DN:', dn);
    }
    return this.commissionReportsService.getNavalMatrizGroup(dn).pipe(
      map(grupo => {
        if (!grupo || grupo.trim() === '') {
          throw new Error(`Não foi possível obter o grupo de comissão para o DN ${dn}. Resposta vazia.`);
        }
        return grupo;
      }),
      tap(grupo => {
        // Guarda no cache após obter com sucesso
        this.cachedGrupoComission = grupo;
        if (this.configService.isDebugEnabled()) {
          console.log('💾 Grupo de comissão salvo no cache:', grupo);
        }
      }),
      catchError(error => {
        const errorMessage = error.message || `Erro ao buscar grupo de comissão para o DN ${dn}`;
        console.error('❌ Erro ao obter grupo de comissão:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  // Método principal para enviar mensagem
  // Busca o grupo de comissão automaticamente antes de enviar
  sendMessage(question: string, sessionId?: string): Observable<ChatbotResponse> {
    return this.getGrupoComission().pipe(
      switchMap(grupoComission => {
        // Gera o sessionId usando o grupo obtido
        const currentSessionId = sessionId || this.generateSessionId(grupoComission);

        const request: ChatbotRequest = {
          CD_DN: this.selectedDnService.getSelectedDn(),
          NM_GRP_COMISSION: grupoComission,
          question: question,
          session_id: currentSessionId
        };

  if (this.configService.isDebugEnabled()) {
          console.log('Enviando pergunta para backend (chatbot) - SessionId:', currentSessionId);
          console.log('Pergunta:', question.substring(0, 50) + '...');
        }

        // 🔹 Usar método post herdado do BaseApiService
        return this.post<ChatbotResponse>('/chatbot/enviar', request).pipe(
          tap(response => {
            if (this.configService.isDebugEnabled()) {
              console.log('✅ Resposta recebida do chatbot via backend - SessionId:', response.session_id);
            }
          }),
          catchError(error => {
            console.error('❌ Erro ao enviar pergunta para o chatbot:', error);
            const errorMessage = error.error?.error || 'Erro ao comunicar com o chatbot';
            return throwError(() => new Error(errorMessage));
          })
        );
      })
    );
  }
}
